import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import { Close } from '@mui/icons-material';
import Description from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';
import { formStyles } from '../../util/styles';
import { updateCase as dexieUpdateCase } from '../../db/actions';
import { useAlert } from '../../util/alertDialogContext';
import CategoryConfigEditor from '../../components/Demographics/CategoryConfigEditor';
import ScoreDefinitionsEditor from '../../components/Demographics/ScoreDefinitionsEditor';
import ApplyTemplateModal from '../Templates/ApplyTemplateModal';

// Only the fields this modal actually edits — comparing the full case
// objects would also flag unrelated concurrent changes (e.g. a juror moved
// by someone else while this was open) as "unsaved" in this modal.
const scoringFields = (obj) => JSON.stringify({
  demographicCategories: obj?.demographicCategories || [],
  demographicCategoryConfigs: obj?.demographicCategoryConfigs || [],
  scoreDefinitions: obj?.scoreDefinitions || [],
  scoringRules: obj?.scoringRules || [],
});

/**
 * Focused editor for a case's demographic categories and score/scoring-rule
 * definitions, split out of EditModal so that "edit case details" isn't
 * cluttered with the (potentially long) categories + scores UI. Wider than
 * EditModal to give the nested scoring-rules tables room to breathe.
 */
const CategoryScoringModal = ({ open, onClose, caseData }) => {
  const classes = formStyles();
  const navigate = useNavigate();
  const { alert } = useAlert();
  const [caseInfo, setCaseInfo] = React.useState(caseData);
  const [applyTemplateOpen, setApplyTemplateOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);

  React.useEffect(() => {
    if (open && caseData) {
      setCaseInfo(caseData);
      setSaveError(null);
    }
  }, [open, caseData]);

  // Normalize the case's category shape (plain name list + additive
  // per-category options config) into what ScoreDefinitionsEditor expects,
  // matching the same {name, options} shape templates provide.
  const scoreCategories = React.useMemo(() => {
    const configsByName = {};
    for (const config of caseInfo?.demographicCategoryConfigs || []) {
      if (config && config.name) {
        configsByName[config.name] = config;
      }
    }
    return (caseInfo?.demographicCategories || []).map((name) => ({
      name,
      options: configsByName[name]?.options || [],
    }));
  }, [caseInfo?.demographicCategories, caseInfo?.demographicCategoryConfigs]);

  const handleClose = () => {
    onClose();
  };

  const goToManageTemplates = () => {
    if (scoringFields(caseData) !== scoringFields(caseInfo)) {
      alert({
        title: 'Discard unsaved changes?',
        msg: 'You have unsaved category or scoring changes on this case. Leaving for "Manage templates" will discard them.',
        onSubmit: () => navigate('/templates'),
        variant: 'danger',
      });
    } else {
      navigate('/templates');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await dexieUpdateCase(caseData._id, {
        demographicCategories: caseInfo.demographicCategories || [],
        demographicCategoryConfigs: caseInfo.demographicCategoryConfigs || [],
        scoreDefinitions: caseInfo.scoreDefinitions || [],
        scoringRules: caseInfo.scoringRules || [],
      });
      setSaving(false);
      onClose();
    } catch (err) {
      console.error('Failed to update case categories/scoring via Dexie:', err);
      setSaving(false);
      setSaveError('Failed to save categories/scoring. Your changes are still here — please try again.');
    }
  };

  if (!caseData) {
    return null;
  }

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        aria-labelledby="category-scoring-modal-title"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Categories &amp; Scoring
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
            size="large">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Collapse in={!!saveError}>
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          </Collapse>
          <Grid container spacing={2}>
            <Grid size={12}>
              <CategoryConfigEditor
                categories={caseInfo.demographicCategories || []}
                configs={caseInfo.demographicCategoryConfigs || []}
                onChange={(nextCategories, nextConfigs) => {
                  setCaseInfo((prev) => ({
                    ...prev,
                    demographicCategories: nextCategories,
                    demographicCategoryConfigs: nextConfigs,
                    // A rule referencing a deleted category would otherwise
                    // become a permanently-inactive "no answer" delta that
                    // silently applies to every juror (the category can
                    // never have an entry again once it's gone).
                    scoringRules: (prev.scoringRules || []).filter(
                      (rule) => nextCategories.includes(rule.categoryName),
                    ),
                  }));
                }}
              />
              <Grid container spacing={1} style={{ marginTop: 4 }}>
                <Grid>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Description />}
                    onClick={() => setApplyTemplateOpen(true)}
                  >
                    Apply a template
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    size="small"
                    variant="text"
                    onClick={goToManageTemplates}
                  >
                    Manage templates
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <ScoreDefinitionsEditor
                categories={scoreCategories}
                scoreDefinitions={caseInfo.scoreDefinitions || []}
                scoringRules={caseInfo.scoringRules || []}
                onChange={(nextScoreDefinitions, nextScoringRules) => {
                  setCaseInfo({
                    ...caseInfo,
                    scoreDefinitions: nextScoreDefinitions,
                    scoringRules: nextScoringRules,
                  });
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button onClick={handleClose} color="primary">Cancel</Button>
        </DialogActions>
      </Dialog>
      <ApplyTemplateModal
        open={applyTemplateOpen}
        caseId={caseData._id}
        onClose={() => setApplyTemplateOpen(false)}
        onApplied={(updatedCase) => {
          setCaseInfo((prev) => ({ ...prev, ...updatedCase }));
        }}
      />
    </React.Fragment>
  );
};

export default CategoryScoringModal;
